import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'

import { selectActiveBuilding } from '../../../../reducers/buildingSelectorReducer'
import { useBuildingOptions } from '../../common/hooks/useBuildingOptions'
import { useEventTypeOptions } from '../../common/hooks/useEventTypeOptions'
import { useTrainerOptions } from '../../common/hooks/useTrainerOptions'
import { useSocialLinksOptions } from '../../common/hooks/useSocialLinksOptions'
import {
  getInitialTimeStart,
  getInitialTimeEnd,
  defaultFrequencyOption,
  visibilityDefaultValue,
} from '../../common/helpers'
import { EMAIL_REGEX } from '../../../../helpers/constants'
import { mapEventDtoToFormData } from '../mappers'

const getInitialValues = (timezone, t, advancedTicketingEnabled) => ({
  // Event details
  building: null,
  type: null,
  name: '',
  organizer: null,
  description: '',
  image: null,
  files: [],
  // Date/Time
  place: '',
  timeStart: getInitialTimeStart(timezone),
  timeEnd: getInitialTimeEnd(timezone),
  frequency: defaultFrequencyOption,
  // Tickets
  capacity: 10,
  guestsAmount: 1,
  collectGuestsInfo: false,
  tickets: [
    {
      name: t('eventManagement:eventFormPage.thirdStep.defaultTicketName'),
      price: 0,
      description: t('eventManagement:eventFormPage.thirdStep.defaultTicketDescription'),
      ...(advancedTicketingEnabled && { inactive: false }),
    },
  ],
  // Advanced - attendees
  householdMembersAllowed: false,
  // Advanced - rsvp
  collectRSVP: false,
  signUpLink: '',
  hideAttendees: false,
  // Advanced - Communication
  automatedRemindersToggle: false,
  remindRsvp: false,
  remindRsvpValues: [''],
  remindAll: false,
  remindAllValues: [''],
  askForPostEventReview: false,
  positiveReview: false,
  socialLinkService: '',
  replyTo: '',
  isPrivate: visibilityDefaultValue,
})

const makeValidationSchema = (t) =>
  Yup.object().shape({
    building: Yup.object()
      .nullable()
      .required(t('eventManagement:eventFormPage.validation.buildingRequired')),
    type: Yup.object()
      .nullable()
      .required(t('eventManagement:eventFormPage.validation.typeRequired')),
    name: Yup.string().required(t('eventManagement:eventFormPage.validation.nameRequired')),
    organizer: Yup.object()
      .nullable()
      .required(t('eventManagement:eventFormPage.validation.facilitatorRequired')),
    description: Yup.string().required(
      t('eventManagement:eventFormPage.validation.descriptionRequired'),
    ),
    image: Yup.object()
      .nullable()
      .required(t('eventManagement:eventFormPage.validation.photoRequired')),
    place: Yup.string().required(t('eventManagement:eventFormPage.validation.locationRequired')),
    timeStart: Yup.object().required(
      t('eventManagement:eventFormPage.validation.dateTimeRequired'),
    ),
    cost: Yup.string().required(t('eventManagement:eventFormPage.validation.costRequired')),
    guestsAmount: Yup.number().test(
      'max-guest-capacity',
      t('eventManagement:eventFormPage.thirdStep.guestsAmountError'),
      function (value) {
        const { capacity } = this.parent
        return value <= capacity
      },
    ),
    replyTo: Yup.string().when('positiveReview', {
      is: true,
      then: (schema) =>
        schema
          .required(t('eventManagement:eventFormPage.validation.replyToRequired'))
          .matches(EMAIL_REGEX, t('eventManagement:eventFormPage.validation.invalidEmail')),
    }),
  })

export const useEventFormState = ({ userData, eventData, t }) => {
  const selectedBuilding = useSelector(selectActiveBuilding)
  const selectedBuildingId = selectedBuilding ? selectedBuilding.id : null
  const selectedBuildingTimezone = selectedBuilding?.timezone
  const advancedTicketingEnabled = selectedBuilding?.eventTicketsAvailable

  const { buildingOptions, isLoading: isBuildingOptionsLoading } = useBuildingOptions(userData)
  const { eventTypeOptions, isLoading: isEventTypeOptionsLoading } = useEventTypeOptions(userData)
  const { trainerOptions, isLoading: isTrainerOptionsLoading } = useTrainerOptions(userData)
  const { isLoading: isSocialLinksLoading } = useSocialLinksOptions(selectedBuildingId)

  const { availableOptions: socialLinkOptions } = useSocialLinksOptions(selectedBuildingId, true)

  const initialValues = useMemo(() => {
    return eventData
      ? mapEventDtoToFormData({
          eventData,
          trainerOptions,
          buildingOptions,
          eventTypeOptions,
        })
      : getInitialValues(selectedBuildingTimezone, t, advancedTicketingEnabled)
  }, [eventData, buildingOptions, eventTypeOptions, trainerOptions, t])

  const validationSchema = useMemo(() => makeValidationSchema(t), [t])

  return {
    initialValues,
    validationSchema,
    buildingOptions,
    eventTypeOptions,
    trainerOptions,
    availableOptions: socialLinkOptions,
    areOptionsLoading:
      isBuildingOptionsLoading ||
      isEventTypeOptionsLoading ||
      isTrainerOptionsLoading ||
      isSocialLinksLoading,
  }
}
